import React from 'react';
import Modal from '../general/Modal';
import { novaThemes, NovaThemeName } from './novaTheme';
import { NovaCloseIcon } from './novaIcons';

interface NovaInfoDialogProps {
  open: boolean;
  theme: NovaThemeName;
  searchTerm?: string;
  createdAt?: number;
  sourceCount: number;
  published12mFilter?: boolean;
  onClose: () => void;
}

const formatCreatedDate = (timestamp?: number): string =>
  timestamp
    ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(timestamp))
    : '—';

// The buildout's "Info → response details" spec: provenance for the answer — query,
// date, source count and the active time window.
const NovaInfoDialog: React.FC<NovaInfoDialogProps> = ({
  open,
  theme,
  searchTerm,
  createdAt,
  sourceCount,
  published12mFilter,
  onClose,
}) => {
  const surfaces = novaThemes[theme];

  const rows: { label: string; value: string }[] = [
    { label: 'Search term', value: searchTerm || '—' },
    { label: 'Created', value: formatCreatedDate(createdAt) },
    { label: 'Sources', value: sourceCount > 0 ? `${sourceCount} Stylus report${sourceCount === 1 ? '' : 's'}` : 'None' },
    { label: 'Window', value: published12mFilter ? 'Last 12 months' : 'All time' },
  ];

  return (
    <Modal open={open} onClose={onClose} zIndex={1000000} contanerClassName="w-[360px] max-w-[92vw] !rounded-[20px] p-0">
      <div
        className="rounded-[20px] border p-6"
        style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder, color: surfaces.ink.body }}
      >
        <div className="mb-5 flex items-center justify-between">
          <div className="text-[19px] font-semibold tracking-[-0.3px]" style={{ color: surfaces.ink.heading }}>
            Response details
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-[30px] w-[30px] flex-none cursor-pointer items-center justify-center rounded-[9px] transition-opacity hover:opacity-70"
            style={{ color: surfaces.ink.secondary }}
          >
            <NovaCloseIcon className="h-[17px] w-[17px]" />
          </button>
        </div>

        <div className="flex flex-col">
          {rows.map(({ label, value }, index) => (
            <div
              key={label}
              className="flex items-start justify-between gap-4 border-t py-3"
              style={{
                borderColor: surfaces.surfaces.hairline,
                borderBottomWidth: index === rows.length - 1 ? 1 : 0,
                borderBottomStyle: index === rows.length - 1 ? 'solid' : undefined,
                borderBottomColor: index === rows.length - 1 ? surfaces.surfaces.hairline : undefined,
              }}
            >
              <span className="flex-none text-[13.5px]" style={{ color: surfaces.ink.secondary }}>
                {label}
              </span>
              <span className="text-right text-sm font-medium" style={{ color: surfaces.ink.heading }}>
                {value}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-4 text-[12.5px] leading-[18px]" style={{ color: surfaces.ink.secondary }}>
          AI-generated from Stylus intelligence — not authored by a Stylus expert.
        </div>
      </div>
    </Modal>
  );
};

export default NovaInfoDialog;
